/**
 * Brand deal sheet editor — /brands/:id/deal/:year
 *
 * One sheet per brand per year: the commercial terms exactly as worded
 * next to the marketing plan (lines from the rate card / bundles, tied
 * to moments). Prices default to the rate card but are per-sheet. Can be
 * shared read-only with the brand at /deal/<token> (PublicDeal.tsx).
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, IconButton } from '../components/ui/Button';
import { Card, CardHead } from '../components/ui/Card';
import { Checkbox } from '../components/ui/Checkbox';
import { CommitInput } from '../components/ui/CommitInput';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Field } from '../components/ui/Field';
import { Icon } from '../components/ui/Icon';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { useBrand } from '../lib/api/brands';
import {
  BrandDeal as Deal,
  DEAL_STATUS_LABEL,
  DealLine,
  DealStatus,
  reachLine,
  useBrandDeals,
  useDealMutations,
  useReachSummary,
} from '../lib/api/deals';
import {
  Moment,
  PERIOD_LABEL,
  PlacementRate,
  RateBundle,
  fmtEur,
  useBundles,
  useMoments,
  useRates,
} from '../lib/api/rateCard';

const STYLES = `
.deal { max-width: 1180px; }
.deal .grid { display: grid; grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr); gap: 16px; align-items: start; }
@media (max-width: 980px) { .deal .grid { grid-template-columns: 1fr; } }
.deal .body { padding: 14px 16px 16px; }
.deal .body .field:last-child { margin-bottom: 0; }
.deal .terms { display: grid; gap: 0; }
.deal table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.deal th:first-child, .deal td:first-child { padding-left: 16px; }
.deal th:last-child, .deal td:last-child { padding-right: 16px; }
.deal th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); font-weight: 600; padding: 8px 10px; border-bottom: 1px solid var(--border); white-space: nowrap; }
.deal td { padding: 8px 10px; border-bottom: 1px solid var(--border-soft); vertical-align: top; }
.deal td.num, .deal th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.deal .line-label { font-weight: 550; color: var(--ink); }
.deal .line-sub { font-size: 11.5px; color: var(--ink-4); margin-top: 2px; }
.deal .line-reach { font-size: 11.5px; color: var(--ink-3); margin-top: 3px; }
.deal .cell-input { width: 84px; padding: 4px 8px; font: inherit; font-size: 13px; border: 1px solid transparent; border-radius: 6px; background: transparent; text-align: right; font-variant-numeric: tabular-nums; }
.deal .cell-input.qty { width: 52px; text-align: center; }
.deal .cell-input.text { width: 100%; min-width: 160px; text-align: left; }
.deal .cell-input:hover, .deal .cell-select:hover { border-color: var(--border); background: var(--bg-elevated); }
.deal .cell-input:focus { outline: none; border-color: var(--sitewide); background: var(--bg-elevated); box-shadow: 0 0 0 3px rgba(59, 92, 255, 0.12); }
.deal .cell-select { padding: 4px 6px; font: inherit; font-size: 12.5px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: var(--ink-2); max-width: 150px; }
.deal .off-list { color: var(--warn); font-size: 10.5px; display: block; }
.deal .totals { display: grid; grid-template-columns: 1fr auto; gap: 4px 18px; padding: 12px 16px 4px; font-size: 13.5px; }
.deal .totals .k { color: var(--ink-3); }
.deal .totals .v { text-align: right; font-variant-numeric: tabular-nums; }
.deal .totals .big { font-size: 18px; font-weight: 700; }
.deal .status-select { font: inherit; font-size: 13px; padding: 6px 10px; border-radius: 8px; border: 1px solid var(--border); background: var(--bg-elevated); }
.deal .share-box { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; font-size: 12.5px; color: var(--ink-3); }
.deal .share-box code { font-size: 12px; background: var(--bg-subtle); padding: 2px 6px; border-radius: 4px; }
.deal .picker { display: grid; gap: 6px; max-height: 360px; overflow: auto; }
.deal .picker button { text-align: left; border: 1px solid var(--border); background: var(--bg-elevated); border-radius: 8px; padding: 8px 12px; font: inherit; cursor: pointer; display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; }
.deal .picker button:hover { border-color: var(--ink-4); }
.deal .picker .p { font-size: 12.5px; color: var(--ink-3); white-space: nowrap; }
.deal .picker .d { font-size: 11.5px; color: var(--ink-4); }
.deal .picker h4 { margin: 8px 0 2px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-4); }
.deal .empty { padding: 20px 16px; font-size: 13px; color: var(--ink-4); }
.deal .reach-note { font-size: 11.5px; color: var(--ink-4); padding: 8px 16px 14px; }
`;

const KINDS: DealStatus[] = ['draft', 'proposed', 'agreed'];

export const BrandDealPage = () => {
  const { id, year: yearParam } = useParams<{ id: string; year: string }>();
  const navigate = useNavigate();
  const year = parseInt(yearParam || '', 10) || new Date().getFullYear() + 1;
  const brand = useBrand(id);
  const deals = useBrandDeals(id, year);
  const m = useDealMutations();
  const toast = useToast();
  const deal = deals.data?.[0];

  return (
    <div className="deal">
      <style>{STYLES}</style>
      <div className="page-header">
        <div>
          <button type="button" className="link" onClick={() => navigate(`/brands/${id}`)} style={{ fontSize: 12 }}>
            ← {brand.data?.name ?? 'Brand'}
          </button>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton aria-label="Previous year" onClick={() => navigate(`/brands/${id}/deal/${year - 1}`)}><Icon name="chevronLeft" size={14} /></IconButton>
            Deal sheet {year}
            <IconButton aria-label="Next year" onClick={() => navigate(`/brands/${id}/deal/${year + 1}`)}><Icon name="chevronRight" size={14} /></IconButton>
          </h1>
        </div>
        {deal && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              className="status-select"
              value={deal.status}
              onChange={(e) => m.patch.mutate({ id: deal.id, status: e.target.value as DealStatus })}
            >
              {KINDS.map((k) => <option key={k} value={k}>{DEAL_STATUS_LABEL[k]}</option>)}
            </select>
            {deal.share_enabled && (
              <Button variant="ghost" onClick={() => window.open(deal.public_path, '_blank')}>
                <Icon name="link" size={13} /> Open brand view
              </Button>
            )}
          </div>
        )}
      </div>

      {deals.isLoading ? (
        <div className="empty">Loading…</div>
      ) : !deal ? (
        <Card>
          <div className="empty" style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
            <div>No deal sheet for {brand.data?.name ?? 'this brand'} in {year} yet.</div>
            <Button
              variant="primary"
              disabled={!id || m.create.isPending}
              onClick={() => id && m.create.mutate({ brand: id, year }, { onError: () => toast.push('Could not create the deal sheet') })}
            >
              <Icon name="plus" size={13} /> Start the {year} deal sheet
            </Button>
          </div>
        </Card>
      ) : (
        <DealEditor deal={deal} />
      )}
    </div>
  );
};

export default BrandDealPage;

function DealEditor({ deal }: { deal: Deal }) {
  const m = useDealMutations();
  const toast = useToast();
  const rates = useRates();
  const bundles = useBundles();
  const moments = useMoments(deal.year);
  const reach = useReachSummary();
  const [picker, setPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [removeLine, setRemoveLine] = useState<DealLine | null>(null);

  const save = (patch: Partial<Deal>) =>
    m.patch.mutate({ id: deal.id, ...patch }, { onError: () => toast.push('Could not save') });
  const saveLine = (line: DealLine, patch: Partial<DealLine>) =>
    m.patchLine.mutate({ id: line.id, ...patch }, { onError: () => toast.push('Could not save the line') });

  const total = deal.agreed_total != null ? deal.agreed_total : deal.lines_total;
  const publicUrl = deal.public_path ? `${window.location.origin}${deal.public_path}` : '';

  return (
    <>
      <div className="grid">
        <div style={{ display: 'grid', gap: 16 }}>
          <Card>
            <CardHead title="Commercial terms" sub="Write them exactly as agreed. Free text on purpose." />
            <div className="body terms">
              <Field label="Stock-order discount"><CommitInput value={deal.stock_discount} placeholder="e.g. 25% + 5% above €10k" onCommit={(v) => save({ stock_discount: v })} /></Field>
              <Field label="Online discount"><CommitInput value={deal.online_discount} placeholder="e.g. max 20% during promos" onCommit={(v) => save({ online_discount: v })} /></Field>
              <Field label="Sell-in target"><CommitInput value={deal.sell_in_target} placeholder="e.g. €60k purchases" onCommit={(v) => save({ sell_in_target: v })} /></Field>
              <Field label="Sell-out target"><CommitInput value={deal.sell_out_target} placeholder="e.g. +15% vs 2026" onCommit={(v) => save({ sell_out_target: v })} /></Field>
              <Field label="Promo periods"><CommitInput multiline rows={3} value={deal.promo_periods} placeholder="e.g. Spring refresh, Black Friday, Christmas" onCommit={(v) => save({ promo_periods: v })} /></Field>
              <Field label="Settlement"><CommitInput value={deal.settlement_note} placeholder="e.g. credit note on a future invoice" onCommit={(v) => save({ settlement_note: v })} /></Field>
            </div>
          </Card>
          <Card>
            <CardHead title="Share with the brand" />
            <div className="body">
            <div className="share-box">
              <Checkbox checked={deal.share_enabled} onChange={(on) => m.share.mutate({ id: deal.id, on })} />
              <span>{deal.share_enabled ? 'Link is active' : 'Turn on to get a read-only link'}</span>
            </div>
            {deal.share_enabled && (
              <div className="share-box" style={{ marginTop: 8 }}>
                <code>{publicUrl}</code>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(publicUrl); toast.push('Link copied'); } catch { toast.push('Could not copy'); }
                  }}
                >
                  <Icon name="copy" size={13} /> Copy
                </Button>
              </div>
            )}
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 12.5, cursor: 'pointer' }}>
              <Checkbox checked={deal.show_prices_publicly} onChange={(v) => save({ show_prices_publicly: v })} />
              Show prices on the brand view
            </label>
            </div>
          </Card>
          <Card>
            <CardHead title="Internal notes" sub="Never shown to the brand." />
            <div className="body">
              <CommitInput multiline rows={4} value={deal.notes} onCommit={(v) => save({ notes: v })} />
            </div>
          </Card>
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <Card>
            <CardHead
              title="Marketing plan"
              sub="Bundles and placements from the rate card. Prices start at the card price and can be adjusted here for this brand only."
              action={<Button variant="primary" onClick={() => setPicker(true)}><Icon name="plus" size={13} /> Add line</Button>}
            />
            <div className="body" style={{ paddingBottom: 0 }}>
              <Field label="Intro for the brand" hint="Shown at the top of the shared sheet.">
                <CommitInput multiline rows={2} value={deal.intro} placeholder={`What we propose for ${deal.brand_name} in ${deal.year}…`} onCommit={(v) => save({ intro: v })} />
              </Field>
            </div>
            {deal.lines.length === 0 ? (
              <div className="empty">No lines yet. Add a bundle or a placement.</div>
            ) : (
              <table style={{ marginTop: 10 }}>
                <thead>
                  <tr>
                    <th>What</th>
                    <th>When</th>
                    <th style={{ textAlign: 'center' }}>Qty</th>
                    <th className="num">Unit price</th>
                    <th className="num">Total</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {deal.lines.map((l) => {
                    const offList = l.list_unit_price != null && parseFloat(l.list_unit_price) !== parseFloat(l.unit_price);
                    const r = reachLine(reach.data, l.asset_type, l.category);
                    return (
                      <tr key={l.id}>
                        <td>
                          <div className="line-label">{l.label}</div>
                          <div className="line-sub">
                            {l.bundle ? 'Bundle' : l.period ? PERIOD_LABEL[l.period as keyof typeof PERIOD_LABEL] ?? l.period : ''}
                          </div>
                          {r && <div className="line-reach">{r}</div>}
                          <CommitInput className="cell-input text" value={l.note} placeholder="Note for the brand…" onCommit={(v) => saveLine(l, { note: v })} />
                        </td>
                        <td>
                          <select className="cell-select" value={l.moment ?? ''} onChange={(e) => saveLine(l, { moment: e.target.value ? parseInt(e.target.value, 10) : null })}>
                            <option value="">Whole year / tbd</option>
                            {(moments.data ?? []).map((mo) => <option key={mo.id} value={mo.id}>{mo.name}</option>)}
                          </select>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <CommitInput className="cell-input qty" type="number" min={1} value={String(l.qty)} onCommit={(v) => saveLine(l, { qty: Math.max(1, parseInt(v || '1', 10)) })} />
                        </td>
                        <td className="num">
                          <CommitInput className="cell-input" type="number" min={0} value={String(parseFloat(l.unit_price))} onCommit={(v) => saveLine(l, { unit_price: v === '' ? '0' : v })} />
                          {offList && <span className="off-list">card: {fmtEur(l.list_unit_price!)}</span>}
                        </td>
                        <td className="num"><b>{fmtEur(l.total)}</b></td>
                        <td style={{ textAlign: 'right' }}>
                          <IconButton aria-label="Remove line" onClick={() => setRemoveLine(l)}><Icon name="x" size={13} /></IconButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {deal.lines.length > 0 && (
              <div className="totals">
                <span className="k">Lines at this sheet's prices</span><span className="v">{fmtEur(deal.lines_total)}</span>
                {parseFloat(deal.list_total) !== parseFloat(deal.lines_total) && (
                  <><span className="k">Same lines at rate-card prices</span><span className="v">{fmtEur(deal.list_total)}</span></>
                )}
                <span className="k">Agreed total <span style={{ color: 'var(--ink-4)' }}>(leave empty to use the lines)</span></span>
                <span className="v">
                  <CommitInput className="cell-input" type="number" min={0} value={deal.agreed_total != null ? String(parseFloat(deal.agreed_total)) : ''} placeholder={String(parseFloat(deal.lines_total))} onCommit={(v) => save({ agreed_total: v === '' ? null : v })} />
                </span>
                <span className="k big">Total for {deal.year}</span><span className="v big">{fmtEur(total)}</span>
              </div>
            )}
            <ReachFootnote reach={reach.data} />
          </Card>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="ghost" style={{ color: 'var(--warn)' }} onClick={() => setConfirmDelete(true)}>Delete this deal sheet</Button>
          </div>
        </div>
      </div>

      {picker && (
        <LinePicker
          rates={(rates.data ?? []).filter((r) => r.active)}
          bundles={(bundles.data ?? []).filter((b) => b.active)}
          moments={moments.data ?? []}
          onPick={(payload) => {
            m.addLine.mutate({ deal: deal.id, ...payload }, {
              onSuccess: () => toast.push('Line added'),
              onError: () => toast.push('Could not add the line'),
            });
          }}
          onClose={() => setPicker(false)}
        />
      )}
      <ConfirmDialog
        open={!!removeLine}
        danger
        title={`Remove “${removeLine?.label ?? ''}” from the sheet?`}
        confirmLabel="Remove"
        busy={m.removeLine.isPending}
        onClose={() => setRemoveLine(null)}
        onConfirm={() => removeLine && m.removeLine.mutate(removeLine.id, { onSuccess: () => setRemoveLine(null) })}
      />
      <ConfirmDialog
        open={confirmDelete}
        danger
        title={`Delete the ${deal.year} deal sheet for ${deal.brand_name}?`}
        description="All its lines go with it. The share link stops working. Cannot be undone."
        confirmLabel="Delete deal sheet"
        busy={m.remove.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => m.remove.mutate(deal.id, { onSuccess: () => setConfirmDelete(false) })}
      />
    </>
  );
}

function ReachFootnote({ reach }: { reach: ReturnType<typeof useReachSummary>['data'] }) {
  if (!reach) return null;
  if (!reach.configured) return <div className="reach-note">Reach figures appear here once mosy is connected to perfy.</div>;
  if (reach.error) return <div className="reach-note">Reach figures unavailable right now ({reach.error}).</div>;
  const anyData = Object.keys(reach.page_reach).length > 0 || Object.keys(reach.placement_types).length > 0;
  if (!anyData) return <div className="reach-note">No reach data yet — figures appear once the storefront has reported a few weeks.</div>;
  return (
    <div className="reach-note">
      Reach = weekly averages over the last {reach.weeks} weeks from the storefront{reach.through ? `, data through ${reach.through}` : ''}. Visits exclude visitors who declined cookies; measured views and clicks count everyone.
    </div>
  );
}

function LinePicker({
  rates, bundles, moments, onPick, onClose,
}: {
  rates: PlacementRate[];
  bundles: RateBundle[];
  moments: Moment[];
  onPick: (payload: { rate?: number; bundle?: number; moment: number | null; qty: number }) => void;
  onClose: () => void;
}) {
  const [moment, setMoment] = useState<number | null>(null);
  const byCategory = useMemo(() => {
    const map = new Map<string, PlacementRate[]>();
    rates.forEach((r) => { if (!map.has(r.category)) map.set(r.category, []); map.get(r.category)!.push(r); });
    return [...map.entries()];
  }, [rates]);
  const priceOf = (r: PlacementRate) => (parseFloat(r.price) > 0 ? `${fmtEur(r.price)} ${PERIOD_LABEL[r.period]}` : r.price_note || 'on request');

  return (
    <Modal open onClose={onClose} title="Add to the plan" maxWidth={560} footer={<Button variant="ghost" onClick={onClose}>Done</Button>}>
      <Field label="For which moment?" hint="You can change it per line afterwards.">
        <select className="input" value={moment ?? ''} onChange={(e) => setMoment(e.target.value ? parseInt(e.target.value, 10) : null)}>
          <option value="">Whole year / to be decided</option>
          {moments.map((mo) => <option key={mo.id} value={mo.id}>{mo.name}</option>)}
        </select>
      </Field>
      <div className="picker" style={{ marginTop: 10 }}>
        <h4>Bundles</h4>
        {bundles.map((b) => (
          <button key={b.id} type="button" onClick={() => onPick({ bundle: b.id, moment, qty: 1 })}>
            <span><div>{b.name}</div><div className="d">{b.blurb}</div></span>
            <span className="p">{parseFloat(b.price) === 0 ? 'Free' : fmtEur(b.price)}</span>
          </button>
        ))}
        {byCategory.map(([cat, list]) => (
          <div key={cat} style={{ display: 'contents' }}>
            <h4>{cat}</h4>
            {list.map((r) => (
              <button key={r.id} type="button" onClick={() => onPick({ rate: r.id, moment, qty: 1 })}>
                <span><div>{r.label}</div>{r.description && <div className="d">{r.description}</div>}</span>
                <span className="p">{priceOf(r)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  );
}
